import { useState, type ReactNode } from "react";

import { cx } from "@/lib/cx";
import { Chip } from "./Chip";
import { FormField } from "./FormField";
import { control, type ControlContext, type ControlSize } from "./control";

/**
 * A set of short values entered one at a time — tags, labels, owners.
 *
 * It replaces a bare comma-separated `<input>` in an app where a tag is a pill
 * on every other surface: a typo produced a brand-new tag silently, and there
 * was no way to see which tags already existed. Every token is a {@link Chip},
 * so the same word looks the same here, on a card and in a filter bar.
 *
 * Three behaviours are deliberate and were each got wrong somewhere:
 *
 * 1. **Nothing is committed on blur.** With a suggestion row directly
 *    underneath, "type `ui`, then click `#uikit`" would silently produce *two*
 *    tokens. Half-typed text gets its own explicit `＋ #ui` chip instead, so
 *    committing it is a click rather than a guess.
 * 2. **Return and comma commit, and stop there.** Both `preventDefault` and
 *    `stopPropagation`: this field is usually inside a form whose ⌘↵/Return
 *    saves, and one press should do one thing.
 * 3. **Backspace on an empty field takes the last token back** — what every
 *    token field does and what fingers expect.
 */

export type TokenFieldProps = {
  /** The field's caption. Rendered by {@link FormField} as a `role="group"`. */
  label: ReactNode;
  tokens: readonly string[];
  onChange: (next: string[]) => void;
  /** Every value already in use, offered under the field as you type. */
  suggestions?: readonly string[];
  /**
   * Drawn in front of every token and stripped from anything typed, so `#ui`
   * and `ui` are the same tag. The stored values never carry it.
   */
  prefix?: string;
  placeholder?: string;
  hint?: ReactNode;
  /** How many suggestions to offer at once. */
  maxSuggestions?: number;
  /** The container this sits in — see `control.ts`. */
  on?: ControlContext;
  size?: ControlSize;
  maxLength?: number;
  disabled?: boolean;
  className?: string;
};

export function TokenField({
  label,
  tokens,
  onChange,
  suggestions = [],
  prefix = "",
  placeholder = "Add one…",
  hint = "Return adds one. Backspace takes the last one back.",
  maxSuggestions = 6,
  on = "panel",
  size = "md",
  maxLength = 40,
  disabled = false,
  className,
}: TokenFieldProps) {
  const [entry, setEntry] = useState("");

  const clean = (raw: string) => {
    const trimmed = raw.trim();
    return prefix && trimmed.startsWith(prefix) ? trimmed.slice(prefix.length) : trimmed;
  };

  const add = (raw: string) => {
    const token = clean(raw);
    if (token === "" || tokens.includes(token)) {
      setEntry("");
      return;
    }
    onChange([...tokens, token]);
    setEntry("");
  };

  const typed = clean(entry);
  const needle = typed.toLowerCase();
  const offered = suggestions
    .filter(
      (token) => !tokens.includes(token) && (needle === "" || token.toLowerCase().includes(needle)),
    )
    .slice(0, maxSuggestions);
  const canAddTyped = typed !== "" && !tokens.includes(typed) && !offered.includes(typed);

  return (
    <FormField label={label} as="group" hint={hint} className={className}>
      {({ describedBy }) => (
        <div className="flex flex-col gap-2">
          {tokens.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {tokens.map((token) => (
                <Chip
                  key={token}
                  label={`${prefix}${token}`}
                  size="sm"
                  disabled={disabled}
                  onDismiss={() => onChange(tokens.filter((entryToken) => entryToken !== token))}
                  dismissLabel={`Remove ${prefix}${token}`}
                />
              ))}
            </div>
          ) : null}

          <input
            value={entry}
            aria-label={typeof label === "string" ? `Add to ${label.toLowerCase()}` : "Add one"}
            aria-describedby={describedBy}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={disabled}
            onChange={(event) => setEntry(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === ",") {
                event.preventDefault();
                event.stopPropagation();
                add(entry);
                return;
              }
              if (event.key === "Backspace" && entry === "" && tokens.length > 0) {
                onChange(tokens.slice(0, -1));
              }
            }}
            className={control({ size, on })}
          />

          {canAddTyped || offered.length > 0 ? (
            <div className={cx("flex flex-wrap gap-2")}>
              {/* `selection="action"` on both: these add a token and vanish.
                  Chip's default is `toggle`, which announced them as "Add
                  #design, toggle button, not pressed" — a pressed state for a
                  control that has none. */}
              {canAddTyped ? (
                <Chip
                  label={`＋ ${prefix}${typed}`}
                  size="sm"
                  // `accent`, not the legacy `focus`: the same measured pair
                  // (accent-soft + accent-ink, 4.60 light / 5.01 dark) under the
                  // name new work uses. See `tones.ts`.
                  tone="accent"
                  disabled={disabled}
                  selection="action"
                  onSelect={() => add(typed)}
                  aria-label={`Add the new entry ${prefix}${typed}`}
                />
              ) : null}
              {offered.map((token) => (
                <Chip
                  key={token}
                  label={`${prefix}${token}`}
                  size="sm"
                  disabled={disabled}
                  selection="action"
                  onSelect={() => add(token)}
                  aria-label={`Add ${prefix}${token}`}
                />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </FormField>
  );
}
